<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-3xl bg-gray-800 text-gray-300 rounded-lg p-5 sm:p-6 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">

      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-700 pb-3">
        <div class="flex items-center gap-2">
          <i class="fas fa-user-cog text-indigo-300 text-2xl"></i>
          <h2 class="text-lg sm:text-xl font-semibold text-indigo-300">เปลี่ยนลักษณะนิสัย</h2>
        </div>
        <button
          @click="closeModal"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200"
          :disabled="isSubmitting"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base"></i>
        </button>
      </div>

      <!-- Resource summary -->
      <div v-if="loading" class="flex items-center justify-center py-10">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
      </div>
      <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
          <span class="text-indigo-200">นิสัยปัจจุบัน</span>
          <span class="font-semibold text-white truncate" :title="currentTraitName || 'ยังไม่กำหนด'">{{ currentTraitName || 'ยังไม่กำหนด' }}</span>
        </div>
        <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
          <span class="text-indigo-200">Cash</span>
          <span class="font-semibold text-white">{{ formatNumber(userCash) }}</span>
        </div>
        <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between sm:col-span-1 col-span-2">
          <span class="text-indigo-200">ค่าเปลี่ยน</span>
          <span class="font-semibold text-white">100 CASH</span>
        </div>
      </div>

      <!-- Trait list -->
      <div v-if="!loading" ref="traitList" id="style-1" class="max-h-80 overflow-y-auto">
        <div
          v-for="trait in availableTraits"
          :key="trait.id"
          @click="selectTrait(trait)"
          class="rounded-md p-3 sm:p-4 mb-2 border transition-all duration-200 cursor-pointer bg-gray-800 hover:bg-gray-700"
          :class="[
            currentTraitName === trait.name ? 'border-indigo-700' : 'border-gray-700',
            selectedTrait && selectedTrait.id === trait.id ? 'ring-2 ring-indigo-700' : '',
            shakeTrait === trait.name ? 'animate-shake border-red-300' : ''
          ]"
          :data-trait="trait.name"
          :aria-selected="selectedTrait && selectedTrait.id === trait.id"
          tabindex="0"
          @keydown.enter="selectTrait(trait)"
          @keydown.space="selectTrait(trait)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="font-semibold text-white truncate">{{ trait.name }}</h3>
                <span
                  v-if="currentTraitName === trait.name"
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-indigo-600 border border-indigo-700 text-white"
                >
                  <i class="fas fa-check"></i> ใช้งานอยู่
                </span>
                <span
                  v-else
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-700 border border-gray-600 text-indigo-200"
                >
                  <i class="fas fa-exchange-alt"></i> เปลี่ยนได้ (100)
                </span>
              </div>

              <p class="text-xs text-indigo-200 mt-1">{{ trait.background }}</p>

              <div class="flex flex-wrap gap-2 mt-2">
                <span v-for="chip in statChips(trait.stats)" :key="chip" class="chip">{{ chip }}</span>
              </div>
            </div>

            <div class="flex flex-col items-end gap-1">
              <i
                :class="[
                  currentTraitName === trait.name ? 'fas fa-check-circle text-green-400 text-lg' : 'fas fa-circle text-gray-600 text-xs'
                ]"
                aria-hidden="true"
              ></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="!loading" class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          class="btn-secondary"
          @click="closeModal"
          :disabled="isSubmitting"
        >
          <i class="fas fa-times text-xs mr-2"></i>ปิด
        </button>
        <button
          v-if="selectedTrait && currentTraitName !== selectedTrait.name"
          class="btn-primary"
          @click="submitChangeTrait"
          :disabled="isSubmitting || userCash < 100"
          aria-label="เปลี่ยนลักษณะนิสัย"
        >
          <span v-if="!isSubmitting"><i class="fas fa-sync-alt text-xs mr-2"></i>ยืนยันเปลี่ยน (100)</span>
          <div v-else class="spinner-sm"></div>
        </button>
        <button
          v-else
          class="btn-disabled"
          disabled
        >
          เลือกนิสัยใหม่เพื่อเปลี่ยน
        </button>
      </div>

      <!-- Messages -->
      <div v-if="errorMessage" class="text-center text-sm"
           :class="errorMessage.includes('สำเร็จ') ? 'text-green-300' : 'text-red-300'">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue';
import { projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'ChangeTraitModal',
  props: { isOpen: { type: Boolean, default: false } },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    // --- State ---
    const availableTraits = ref([
      { id: 1, name: 'ว่องไว',  background: 'เคลื่อนไหวรวดเร็ว หนีซอมบี้ได้ไว',   stats: { str: 3, stamina: 10, int: 0, agi: 5 } },
      { id: 2, name: 'กล้าหาญ', background: 'กล้าเผชิญหน้า ต่อสู้เก่ง',          stats: { str: 6, stamina: 10, int: 0, agi: 0 } },
      { id: 3, name: 'ฉลาด',    background: 'ชาญฉลาด สร้างกับดักเก่ง',            stats: { str: 3, stamina: 10, int: 5, agi: 0 } },
      { id: 4, name: 'อดทน',    background: 'เยือกเย็น อึด',                        stats: { str: 3, stamina: 15, int: 0, agi: 0 } }
    ]);
    const selectedTrait = ref(null);
    const userCash = ref(0);
    const currentTraitName = ref('');
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    const loading = ref(true);
    const traitList = ref(null);
    const shakeTrait = ref(null);

    // --- Utils ---
    const statChips = (s) => {
      const list = [];
      if (s?.stamina > 0) list.push(`+${s.stamina} Stamina`);
      if (s?.str > 0)     list.push(`+${s.str} STR`);
      if (s?.int > 0)     list.push(`+${s.int} INT`);
      if (s?.agi > 0)     list.push(`+${s.agi} AGI`);
      return list;
    };

    const formatNumber = (n) => {
      const num = Number(n);
      if (!Number.isFinite(num)) return '0';
      try { return num.toLocaleString('th-TH'); } catch { return String(num); }
    };

    const scrollToCurrentTrait = () => {
      if (!traitList.value || !currentTraitName.value) return;
      nextTick(() => {
        const el = traitList.value.querySelector(`[data-trait="${currentTraitName.value}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    };

    // --- Data fetching ---
    const fetchUserData = async () => {
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        loading.value = false;
        return;
      }
      playerStore.init(user.value.uid);
      try {
        const userData = playerStore.userData;
        const charPrivateData = playerStore.characterPrivateData;
        if (userData) {
          userCash.value = userData.cash || 0;
        } else {
          errorMessage.value = 'ไม่พบข้อมูลผู้ใช้';
        }
        if (charPrivateData) {
          currentTraitName.value = charPrivateData.traitName || '';
        } else {
          errorMessage.value = 'ไม่พบข้อมูลตัวละคร';
        }
        scrollToCurrentTrait();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', err);
        errorMessage.value = 'ไม่สามารถโหลดข้อมูลได้';
      } finally {
        loading.value = false;
      }
    };

    // --- Interactions ---
    const selectTrait = (trait) => {
      if (isSubmitting.value || trait.name === currentTraitName.value) return;
      selectedTrait.value = trait;
      errorMessage.value = '';
    };

    const closeModal = () => {
      if (isSubmitting.value) return;
      selectedTrait.value = null;
      errorMessage.value = '';
      emit('close');
    };

    const submitChangeTrait = async () => {
      if (!selectedTrait.value || currentTraitName.value === selectedTrait.value.name || userCash.value < 100) {
        errorMessage.value = userCash.value < 100 ? 'เงินไม่เพียงพอ (ต้องใช้ 100 cash)' : 'กรุณาเลือกนิสัยใหม่';
        if (selectedTrait.value) {
          shakeTrait.value = selectedTrait.value.name;
          setTimeout(() => { shakeTrait.value = null; }, 500);
        }
        return;
      }
      isSubmitting.value = true;
      errorMessage.value = '';
      try {
        const fn = projectFunctions.httpsCallable('gameActions');
        await fn({
          action: 'characterActions',
          subAction: 'changeTrait',
          traitId: selectedTrait.value.id,
          traitName: selectedTrait.value.name,
          traitStats: selectedTrait.value.stats
        });
        userCash.value = Math.max(0, (userCash.value || 0) - 100);
        currentTraitName.value = selectedTrait.value.name;
        selectedTrait.value = null;
        scrollToCurrentTrait();
        errorMessage.value = 'เปลี่ยนลักษณะนิสัยสำเร็จ!';
        setTimeout(() => { errorMessage.value = ''; }, 2500);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error changing trait:', err);
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการเปลี่ยนลักษณะนิสัย';
      } finally {
        isSubmitting.value = false;
      }
    };

    // --- Lifecycle ---
    watch(() => props.isOpen, (v) => {
      if (v) {
        loading.value = true;
        fetchUserData();
      }
    });
    onMounted(() => {
      if (props.isOpen) fetchUserData();
    });

    return {
      // state
      availableTraits, selectedTrait, userCash, currentTraitName,
      isSubmitting, errorMessage, loading, traitList, shakeTrait,

      // utils
      statChips, formatNumber,

      // methods
      selectTrait, closeModal, submitChangeTrait
    };
  }
};
</script>

<style scoped>
/* Reusable buttons (ธีมเดียวกับคอมโพเนนต์อื่น ๆ) */
.btn-primary {
  @apply w-full bg-indigo-800 hover:bg-indigo-900 text-white py-2 rounded-md text-sm border border-indigo-900 transition-colors duration-200 flex justify-center items-center;
}
.btn-secondary {
  @apply w-full bg-gray-800 hover:bg-gray-700 text-white py-2 rounded-md text-sm border border-gray-700 transition-colors duration-200 flex justify-center items-center;
}
.btn-disabled {
  @apply w-full bg-gray-800 text-gray-400 py-2 rounded-md text-sm border border-gray-700 cursor-not-allowed;
}

/* Summary chip */
.summary-chip {
  @apply bg-gray-900 border border-gray-800 rounded-md px-3 py-2 text-sm flex items-center justify-between;
}

/* Chip */
.chip { @apply text-2xs sm:text-xs px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-indigo-200 inline-flex items-center; }
.text-2xs { font-size: 0.7rem; }

/* Spinner small */
.spinner-sm {
  width: 1rem; height: 1rem;
  border-top-width: 2px; border-right-width: 2px;
  border-color: rgba(255,255,255,0.9);
  border-style: solid;
  border-bottom-color: transparent; border-left-color: transparent;
  border-radius: 9999px; animation: spin 1s linear infinite; margin: 0 auto;
}

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

@keyframes spin { to { transform: rotate(360deg); } }

.animate-shake { animation: shake 0.5s ease-in-out; }
@keyframes shake {
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
}

/* Scrollbar */
#style-1 { scrollbar-width: thin; scrollbar-color: #4b5563 #1f2937; }
#style-1::-webkit-scrollbar { width: 6px; }
#style-1::-webkit-scrollbar-track { background: #1f2937; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #4b5563; }
</style>